import { AnimatePresence, m } from 'motion/react';
import { formatBytes, formatPercent } from '../core/format';
import type { SessionRecord } from '../engine/db';
import { useApp } from '../state/store';
import { useSession } from './hooks/useSession';
import { CoverageRail } from './parts/CoverageRail';
import { KindIcon, Pause, Play, Trash } from './parts/Icons';
import { stateLabel } from './state-copy';

export function Queue({ compact }: { compact?: boolean }) {
  const records = useApp((s) => s.records);
  const activeId = useApp((s) => s.activeId);
  const list = Object.values(records).sort((a, b) => b.createdAt - a.createdAt);

  return (
    <div className={`queue ${compact ? 'queue--compact' : ''}`}>
      <div className="queue__head">
        <h2 className="queue__title">On this device</h2>
        <span className="queue__count mono">{list.length}</span>
      </div>
      {list.length === 0 ? (
        <p className="queue__empty">
          Nothing saved yet. Paste a link to start. Files you download stay here until you delete
          them.
        </p>
      ) : (
        <ul className="queue__list">
          <AnimatePresence initial={false}>
            {list.map((r) => (
              <m.li
                key={r.id}
                layout="position"
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
              >
                <QueueItem record={r} current={r.id === activeId} />
              </m.li>
            ))}
          </AnimatePresence>
        </ul>
      )}
    </div>
  );
}

function QueueItem({ record, current }: { record: SessionRecord; current: boolean }) {
  const { snap, total, saved, fraction, complete } = useSession(record.id);
  const setActive = useApp((s) => s.setActive);
  const pause = useApp((s) => s.pause);
  const resume = useApp((s) => s.resume);
  const setConfirmRemove = useApp((s) => s.setConfirmRemove);
  const setQueueOpen = useApp((s) => s.setQueueOpen);
  const live = record.state === 'active' || record.state === 'probing';
  const paused = record.state === 'paused' || (record.state === 'recoverableError' && saved > 0);

  return (
    <div className={`qitem ${current ? 'qitem--current' : ''}`} data-state={record.state}>
      <button
        type="button"
        className="qitem__main"
        onClick={() => {
          setActive(record.id);
          setQueueOpen(false);
        }}
        aria-current={current ? 'true' : undefined}
      >
        <KindIcon kind={record.kind} className="qitem__icon" />
        <span className="qitem__text">
          <span className="qitem__name">{record.name}</span>
          <span className="qitem__meta">
            <span className={`dot dot--${record.state}`} aria-hidden="true" />
            {stateLabel(record.state)}
            <span className="mono">
              {' · '}
              {complete || total === null
                ? formatBytes(total ?? saved)
                : `${formatPercent(fraction)} of ${formatBytes(total)}`}
            </span>
          </span>
        </span>
      </button>
      <div className="qitem__rail">
        <CoverageRail
          total={total}
          readable={
            snap?.readable ??
            (complete && total
              ? [{ start: 0, end: total }]
              : saved > 0 && total
                ? [{ start: 0, end: saved }]
                : [])
          }
          inFlight={live ? (snap?.inFlight ?? []) : []}
          variant="mini"
          label={`${record.name}, saved on this device`}
        />
      </div>
      <div className="qitem__actions">
        {live && (
          <button
            type="button"
            className="btn btn--quiet btn--icon"
            onClick={() => pause(record.id)}
            aria-label={`Pause ${record.name}`}
          >
            <Pause weight="fill" />
          </button>
        )}
        {paused && (
          <button
            type="button"
            className="btn btn--quiet btn--icon"
            onClick={() => {
              setActive(record.id);
              setQueueOpen(false);
              void resume(record.id);
            }}
            aria-label={`Resume ${record.name}`}
          >
            <Play weight="fill" />
          </button>
        )}
        <button
          type="button"
          className="btn btn--quiet btn--icon"
          onClick={() => setConfirmRemove(record.id)}
          aria-label={`Delete ${record.name}`}
        >
          <Trash />
        </button>
      </div>
    </div>
  );
}
